<template>
  <Bar
    :data="finalChartData"
    :options="finalChartOptions"
  />
</template>

<script lang="ts">
import 'chart.js/auto'
import { Bar } from 'vue-chartjs'
import type { ChartData as FinalChartData, ChartOptions } from 'chart.js/auto'
import type { ChartData } from '~/types/analytics'
import {
  DEFAULT_CHART_OPTIONS,
  SCALES_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'
import { getColorPalette } from '~/services/chart'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'

export default {
  name: 'ChartBar',
  components: { Bar },
  props: {
    chartData: {
      type: Object as PropType<ChartData>,
      required: true
    }
  },
  computed: {
    finalChartOptions(): ChartOptions<'bar'> {
      return {
        ...DEFAULT_CHART_OPTIONS,
        indexAxis: 'y',
        scales: {
          x: {
            ...SCALES_OPTIONS.x,
            stacked: false,
            ticks: {
              ...SCALES_OPTIONS.x.ticks,
              font: {
                size: 13
              },
              maxTicksLimit: 7,
              callback: value => {
                return Number(value) < 0 ? -value : value
              }
            },
            beginAtZero: true
          },
          y: {
            ...SCALES_OPTIONS.y,
            stacked: true,
            ticks: {
              ...SCALES_OPTIONS.y.ticks,
              maxTicksLimit: 20,
              autoSkip: false,
              font: {
                size: 13
              }
            },
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            reverse: false,
            labels: {
              font: {
                size: 13
              },
              pointStyle: 'rectRounded',
              usePointStyle: true
            }
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              label: context => {
                const value = context.parsed.x
                const positiveOnly = value < 0 ? -value : value
                let retStr = ''
                if (context.datasetIndex === 0) {
                  retStr += `${this.$t(
                    'dashboard.dei.icons.female'
                  )}: ${positiveOnly.toString()}`
                } else {
                  retStr += `${this.$t(
                    'dashboard.dei.icons.male'
                  )}: ${positiveOnly.toString()}`
                }
                return retStr
              }
            }
          }
        }
      }
    },
    finalChartData(): FinalChartData<'bar'> {
      const length = this.chartData.datasets.length
      const colors = getColorPalette(length, Object.values(exportedColors))

      return {
        ...this.chartData,
        labels: this.chartData.labels.map(label => label.formattedValue),
        datasets: this.chartData.datasets.map((dataset, datasetIndex) => {
          const color = colors[datasetIndex]!

          return {
            ...dataset,
            yAxisID: 'y',
            stack: 'main',
            data:
              datasetIndex % 2 === 1
                ? dataset.data
                : dataset.data.map(k => (k === null ? null : -k)),
            backgroundColor: color
          }
        })
      }
    }
  }
}
</script>
